import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DollarSign, Plus, Search, Filter, Download, Eye, Edit2, Trash2,
  CheckCircle, Clock, AlertCircle, RefreshCw, FileText, CreditCard,
  Building, Banknote, Wallet, MoreHorizontal, X
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { formatCurrency as fmtCurrency } from '@/lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentManagement = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [formData, setFormData] = useState({
    entity_type: 'delegate',
    entity_name: '',
    entity_email: '',
    amount: '',
    currency: 'USD',
    payment_method: 'bank_transfer',
    status: 'pending',
    tier_name: '',
    package_description: '',
    due_date: '',
    notes: '',
    reference_number: ''
  });

  // Helper function to get auth token consistently
  const getToken = () => {
    try {
      const authStorage = localStorage.getItem('eventflow-auth');
      if (authStorage) {
        const parsed = JSON.parse(authStorage);
        return parsed?.state?.token || localStorage.getItem('eventflow_token');
      }
    } catch (e) {}
    return localStorage.getItem('eventflow_token');
  };

  const token = getToken();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch payments
  const { data: payments = [], isLoading } = useQuery({
    queryKey: ['payments', eventId, statusFilter, entityTypeFilter],
    queryFn: async () => {
      let url = `${API_URL}/api/events/${eventId}/payments?`;
      if (statusFilter !== 'all') url += `status=${statusFilter}&`;
      if (entityTypeFilter !== 'all') url += `entity_type=${entityTypeFilter}&`;
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error('Failed to fetch payments');
      return res.json();
    },
    enabled: !!eventId
  });

  // Fetch summary
  const { data: summary } = useQuery({
    queryKey: ['payment-summary', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/payments/summary`, { headers });
      if (!res.ok) throw new Error('Failed to fetch summary');
      return res.json();
    },
    enabled: !!eventId
  });

  // Fetch payment methods
  const { data: methodsData } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/payments/methods`, { headers });
      return res.json();
    }
  });

  // Fetch currencies
  const { data: currenciesData } = useQuery({
    queryKey: ['currencies'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/payments/currencies`, { headers });
      return res.json();
    }
  });

  // Fetch tiers for dropdown
  const { data: tiers = [] } = useQuery({
    queryKey: ['tiers', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/tiers`, { headers });
      return res.json();
    },
    enabled: !!eventId
  });

  // Create payment mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/payments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...data, event_id: eventId })
      });
      if (!res.ok) throw new Error('Failed to create payment');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['payments', eventId]);
      queryClient.invalidateQueries(['payment-summary', eventId]);
      setShowAddDialog(false);
      resetForm();
      toast.success('Payment created successfully');
    },
    onError: (err) => toast.error(err.message)
  });

  // Update payment mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/payments/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error('Failed to update payment');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['payments', eventId]);
      queryClient.invalidateQueries(['payment-summary', eventId]);
      setShowEditDialog(false);
      toast.success('Payment updated successfully');
    },
    onError: (err) => toast.error(err.message)
  });

  // Mark as paid mutation
  const markPaidMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/payments/${id}/mark-paid`, {
        method: 'POST',
        headers
      });
      if (!res.ok) throw new Error('Failed to mark as paid');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['payments', eventId]);
      queryClient.invalidateQueries(['payment-summary', eventId]);
      toast.success('Payment marked as paid');
    }
  });

  // Delete payment mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/payments/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error('Failed to delete payment');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['payments', eventId]);
      queryClient.invalidateQueries(['payment-summary', eventId]);
      toast.success('Payment deleted');
    }
  });

  const resetForm = () => {
    setFormData({
      entity_type: 'delegate',
      entity_name: '',
      entity_email: '',
      amount: '',
      currency: 'USD',
      payment_method: 'bank_transfer',
      status: 'pending',
      tier_name: '',
      package_description: '',
      due_date: '',
      notes: '',
      reference_number: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      amount: parseFloat(formData.amount)
    };
    if (selectedPayment) {
      updateMutation.mutate({ id: selectedPayment.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const openEditDialog = (payment) => {
    setSelectedPayment(payment);
    setFormData({
      entity_type: payment.entity_type || 'delegate',
      entity_name: payment.entity_name || '',
      entity_email: payment.entity_email || '',
      amount: payment.amount?.toString() || '',
      currency: payment.currency || 'USD',
      payment_method: payment.payment_method || 'bank_transfer',
      status: payment.status || 'pending',
      tier_name: payment.tier_name || '',
      package_description: payment.package_description || '',
      due_date: payment.due_date || '',
      notes: payment.notes || '',
      reference_number: payment.reference_number || ''
    });
    setShowEditDialog(true);
  };

  const handleExport = async () => {
    try {
      const authToken = getToken();
      if (!authToken) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      toast.info('Generating payment export...');
      const res = await fetch(`${API_URL}/api/events/${eventId}/payments/export`, { 
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (res.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      
      if (res.status === 404) {
        toast.error('No payments found to export.');
        return;
      }
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'Export failed');
        return;
      }
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payments_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export downloaded');
    } catch (err) {
      console.error('Payment export error:', err);
      toast.error('Export failed. Please check your connection.');
    }
  };

  const fetchInvoice = async (paymentId) => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/payments/${paymentId}/invoice`, { headers });
      const data = await res.json();
      setSelectedPayment({ ...selectedPayment, invoice: data });
      setShowInvoiceDialog(true);
    } catch (err) {
      toast.error('Failed to load invoice');
    }
  };

  const filteredPayments = payments.filter(p =>
    p.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.entity_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      partial: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      refunded: 'bg-purple-100 text-purple-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const icons = {
      pending: <Clock className="w-3 h-3 mr-1" />,
      partial: <RefreshCw className="w-3 h-3 mr-1" />,
      paid: <CheckCircle className="w-3 h-3 mr-1" />,
      refunded: <AlertCircle className="w-3 h-3 mr-1" />,
      cancelled: <X className="w-3 h-3 mr-1" />
    };
    return (
      <Badge className={`${styles[status] || styles.pending} flex items-center`}>
        {icons[status]} {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  const getMethodIcon = (method) => {
    const icons = {
      cash: <Banknote className="w-4 h-4" />,
      bank_transfer: <Building className="w-4 h-4" />,
      check: <FileText className="w-4 h-4" />,
      card: <CreditCard className="w-4 h-4" />,
      paypal: <Wallet className="w-4 h-4" />
    };
    return icons[method] || <DollarSign className="w-4 h-4" />;
  };

  const formatCurrency = (amount, currency = 'USD') => fmtCurrency(amount, currency);

  return (
    <div className="space-y-6" data-testid="payment-management">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(summary?.total_amount)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Collected</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(summary?.total_paid)}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{formatCurrency(summary?.total_pending)}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Collection Rate</p>
                <p className="text-2xl font-bold">{(summary?.collection_rate || 0).toFixed(1)}%</p>
              </div>
              <RefreshCw className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex gap-2 flex-1 w-full md:w-auto">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name, invoice, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="payment-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
            <SelectTrigger className="w-32" data-testid="entity-filter">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="delegate">Delegate</SelectItem>
              <SelectItem value="sponsor">Sponsor</SelectItem>
              <SelectItem value="exhibitor">Exhibitor</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          <Button onClick={() => { resetForm(); setSelectedPayment(null); setShowAddDialog(true); }} data-testid="add-payment-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Payment
          </Button>
        </div>
      </div>

      {/* Payments Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="payments-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading...</td>
                  </tr>
                ) : filteredPayments.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">No payments found</td>
                  </tr>
                ) : filteredPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50" data-testid={`payment-row-${payment.id}`}>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm">{payment.invoice_number}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{payment.entity_name}</p>
                        <p className="text-sm text-gray-500">{payment.entity_type} • {payment.entity_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{formatCurrency(payment.amount, payment.currency)}</p>
                        {payment.paid_amount > 0 && payment.paid_amount < payment.amount && (
                          <p className="text-sm text-green-600">Paid: {formatCurrency(payment.paid_amount, payment.currency)}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getMethodIcon(payment.payment_method)}
                        <span className="text-sm capitalize">{payment.payment_method?.replace('_', ' ')}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(payment.status)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {payment.due_date ? new Date(payment.due_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`payment-actions-${payment.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setSelectedPayment(payment); fetchInvoice(payment.id); }}>
                            <FileText className="w-4 h-4 mr-2" /> View Invoice
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditDialog(payment)}>
                            <Edit2 className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          {payment.status !== 'paid' && (
                            <DropdownMenuItem onClick={() => markPaidMutation.mutate(payment.id)}>
                              <CheckCircle className="w-4 h-4 mr-2" /> Mark as Paid
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => {
                              if (window.confirm('Delete this payment?')) {
                                deleteMutation.mutate(payment.id);
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setShowEditDialog(false);
          setSelectedPayment(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedPayment ? 'Edit Payment' : 'Add New Payment'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Entity Type *</label>
                <Select value={formData.entity_type} onValueChange={(v) => setFormData({ ...formData, entity_type: v })}>
                  <SelectTrigger data-testid="entity-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="delegate">Delegate</SelectItem>
                    <SelectItem value="sponsor">Sponsor</SelectItem>
                    <SelectItem value="exhibitor">Exhibitor</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <Input
                  required
                  value={formData.entity_name}
                  onChange={(e) => setFormData({ ...formData, entity_name: e.target.value })}
                  placeholder="Full name or company"
                  data-testid="entity-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <Input
                  type="email"
                  value={formData.entity_email}
                  onChange={(e) => setFormData({ ...formData, entity_email: e.target.value })}
                  placeholder="email@example.com"
                  data-testid="entity-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Amount *</label>
                <Input
                  required
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="amount-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Currency</label>
                <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                  <SelectTrigger data-testid="currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(currenciesData?.currencies || []).map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.code} - {c.symbol}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment Method</label>
                <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                  <SelectTrigger data-testid="method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(methodsData?.methods || []).map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Tier/Package</label>
                <Select value={formData.tier_name || "custom"} onValueChange={(v) => {
                  const tier = tiers.find(t => t.name === v);
                  setFormData({ 
                    ...formData, 
                    tier_name: v === "custom" ? "" : v,
                    amount: tier?.price?.toString() || formData.amount
                  });
                }}>
                  <SelectTrigger data-testid="tier-select">
                    <SelectValue placeholder="Select tier (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom</SelectItem>
                    {tiers.map((t) => (
                      <SelectItem key={t.id} value={t.name}>{t.name} - {formatCurrency(t.price)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Due Date</label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  data-testid="due-date-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Reference Number</label>
                <Input
                  value={formData.reference_number}
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  placeholder="Transaction ID, check number..."
                  data-testid="reference-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Package Description</label>
                <Input
                  value={formData.package_description}
                  onChange={(e) => setFormData({ ...formData, package_description: e.target.value })}
                  placeholder="Description for invoice"
                  data-testid="description-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
                data-testid="notes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowAddDialog(false); setShowEditDialog(false); }}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-payment-btn">
                {selectedPayment ? 'Update Payment' : 'Create Payment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invoice {selectedPayment?.invoice?.invoice_number}</DialogTitle>
          </DialogHeader>
          {selectedPayment?.invoice && (
            <div className="space-y-6 p-4 border rounded-lg bg-white" data-testid="invoice-preview">
              {/* Header */}
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold">{selectedPayment.invoice.company?.name || 'EventFlow'}</h2>
                  <p className="text-gray-500">{selectedPayment.invoice.company?.email}</p>
                </div>
                <div className="text-right">
                  <h3 className="text-xl font-bold">INVOICE</h3>
                  <p className="font-mono">{selectedPayment.invoice.invoice_number}</p>
                  <p className="text-sm text-gray-500">
                    Date: {new Date(selectedPayment.invoice.date).toLocaleDateString()}
                  </p>
                  {selectedPayment.invoice.due_date && (
                    <p className="text-sm text-gray-500">
                      Due: {new Date(selectedPayment.invoice.due_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>

              {/* Bill To */}
              <div className="border-t pt-4">
                <p className="text-sm text-gray-500 mb-1">Bill To:</p>
                <p className="font-medium">{selectedPayment.invoice.customer?.name}</p>
                <p className="text-gray-600">{selectedPayment.invoice.customer?.email}</p>
              </div>

              {/* Event */}
              {selectedPayment.invoice.event?.name && (
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-500 mb-1">Event:</p>
                  <p className="font-medium">{selectedPayment.invoice.event.name}</p>
                </div>
              )}

              {/* Items */}
              <table className="w-full border-t">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 text-left">Description</th>
                    <th className="py-2 text-right">Qty</th>
                    <th className="py-2 text-right">Price</th>
                    <th className="py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPayment.invoice.items?.map((item, i) => (
                    <tr key={i} className="border-b">
                      <td className="py-2">{item.description}</td>
                      <td className="py-2 text-right">{item.quantity}</td>
                      <td className="py-2 text-right">{formatCurrency(item.unit_price, selectedPayment.invoice.currency)}</td>
                      <td className="py-2 text-right">{formatCurrency(item.total, selectedPayment.invoice.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64">
                  <div className="flex justify-between py-1">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(selectedPayment.invoice.subtotal, selectedPayment.invoice.currency)}</span>
                  </div>
                  <div className="flex justify-between py-1 font-bold border-t">
                    <span>Total:</span>
                    <span>{formatCurrency(selectedPayment.invoice.total, selectedPayment.invoice.currency)}</span>
                  </div>
                  {selectedPayment.invoice.paid_amount > 0 && (
                    <div className="flex justify-between py-1 text-green-600">
                      <span>Paid:</span>
                      <span>{formatCurrency(selectedPayment.invoice.paid_amount, selectedPayment.invoice.currency)}</span>
                    </div>
                  )}
                  {selectedPayment.invoice.balance_due > 0 && (
                    <div className="flex justify-between py-1 text-red-600 font-bold">
                      <span>Balance Due:</span>
                      <span>{formatCurrency(selectedPayment.invoice.balance_due, selectedPayment.invoice.currency)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedPayment.invoice.notes && (
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-500 mb-1">Notes:</p>
                  <p>{selectedPayment.invoice.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>Close</Button>
            <Button onClick={() => window.print()}>
              <Download className="w-4 h-4 mr-2" /> Print / Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentManagement;
